import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useWindowSize } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@common/hooks/use-window-size';

import bookBackground from '../../../../../assets/images/book-bg.png';
import {
  ApplicationSection,
  PageLayout,
} from '../../../../../graph/schema-generated-types-educator';
import useGame from '../../../../../hooks/use-game';
import usePagingActivity from '../../../../../hooks/use-paging-activity';
import usePage from '../../../../../hooks/use-page';
import useTelemetry from '../../../../../hooks/use-telemetry';
import FindTheObjectProvider from '../../../../../providers/FindTheObjectProvider';
import { FindTheObjectInterface } from '../../../../../providers/GameProvider/types';
import {
  FONT_GSS_FUTURA_LIGHT,
  FONT_GSS_FUTURA_SEMI_BOLD,
} from '../../../../../themes/defaultTheme';
import { ActivityTypes, TelemetryEvents } from '../../../../../types';
import { getAnimationForPage } from '../../../../../util/animation-util';
import { formatCharacter, formatPageNumber } from '../../../../../util/telemetry-util';

import FindTheObjectGame from './components/FindTheObjectGame';

const FindTheObjectActivity: React.FC = () => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_ACTIVITY_PAGE);
  const activityContainerRef = useRef<HTMLDivElement>(null);
  const [{ currentPage }] = usePage();
  const [{ activityData, currentData }] = useGame<FindTheObjectInterface>();
  const { height: windowHeight, width: windowWidth } = useWindowSize();

  const { onePageBehavior, hasSecondPage } = usePagingActivity(
    currentData as FindTheObjectInterface[],
    activityData,
    'FindTheObjects'
  );

  const singlePage = !hasSecondPage || onePageBehavior;

  const [pagesLoaded, setPagesLoaded] = useState([false, false]);
  const [gameLoaded, setGameLoaded] = useState(false);
  const classes = useStyles({ gameLoaded });

  const secondPageStartingIndex =
    currentData[0]?.objects?.reduce((acc, current) => acc + current.occurrences, 1) ?? 0;

  const FirstPage = (
    <FindTheObjectGame
      key={currentPage}
      {...currentData?.[0]}
      activityContainerRef={activityContainerRef}
      animation={getAnimationForPage(currentData, onePageBehavior)}
      onePageBehaviour={onePageBehavior}
      startingIndex={0}
      gameLoaded={gameLoaded}
      onPageLoaded={() => setPagesLoaded((oldValue) => [true, oldValue[1]])}
    />
  );

  const SecondPage = !singlePage ? (
    <FindTheObjectGame
      key={currentPage + 1}
      {...currentData?.[1]}
      activityContainerRef={activityContainerRef}
      onePageBehaviour={onePageBehavior}
      startingIndex={secondPageStartingIndex}
      gameLoaded={gameLoaded}
      onPageLoaded={() => setPagesLoaded((oldValue) => [oldValue[0], true])}
    />
  ) : null;

  const FirstPageDescription = (
    <Typography key={currentPage} className={classes.pageDescription}>
      {currentData?.[0].contents?.description ?? ''}
    </Typography>
  );

  const SecondPageDescription = (
    <Typography key={currentPage + 1} className={classes.pageDescription}>
      {currentData?.[1].contents?.description ?? ''}
    </Typography>
  );

  const FirstPageNumber = (
    <Typography key={currentPage} className={classes.pageNumber}>
      {currentPage}
    </Typography>
  );

  const SecondPageNumber = (
    <Typography
      key={currentPage + 1}
      className={classes.pageNumber}
      style={{ justifySelf: 'flex-end' }}
    >
      {currentPage + 1}
    </Typography>
  );

  useEffect(() => {
    setGameLoaded(singlePage ? pagesLoaded[0] : pagesLoaded.every((item) => !!item));
  }, [singlePage, pagesLoaded]);

  useEffect(() => {
    setGameLoaded(false);
    setPagesLoaded([false, false]);
  }, [windowHeight, windowWidth, currentPage]);

  useEffect(() => {
    const book = activityData?.book;
    const page = currentData?.[0]?.page;
    if (!book || !page) return;

    const character = { ...book.characters?.[0] };
    if (character) delete character.__typename;

    fireEvent({
      activityType: ActivityTypes.FIND_THE_OBJECTS,
      bookId: book.id,
      bookCode: book.bookCode,
      pageId: page.id,
      pageNumber: formatPageNumber(page.pageNumber),
      pageLayout: hasSecondPage ? PageLayout.DOUBLE_PAGE : PageLayout.SINGLE_PAGE,
      section: ApplicationSection.LETTER_LEARNING,
      character: formatCharacter(character),
    });
  }, [currentData]);

  return (
    <>
      {useMemo(
        () => (
          <FindTheObjectProvider>
            <div
              ref={activityContainerRef}
              className={`${classes.wrapper} ${onePageBehavior && classes.onePageBehavior} `}
            >
              {Array.from([FirstPage, SecondPage])}
              {Array.from([FirstPageDescription, SecondPageDescription])}
              {Array.from([FirstPageNumber, SecondPageNumber])}
            </div>
          </FindTheObjectProvider>
        ),
        [currentData, hasSecondPage, onePageBehavior, gameLoaded]
      )}
    </>
  );
};

const useStyles = makeStyles((theme: any) => ({
  wrapper: {
    transition: ({ gameLoaded }: any) => (gameLoaded ? 'all .5s' : 'unset'),
    opacity: ({ gameLoaded }: any) => (gameLoaded ? 1 : 0),
    height: ({ gameLoaded }: any) => (gameLoaded ? 'max-content' : '100%'),
    width: ({ gameLoaded }: any) => (gameLoaded ? 'max-content' : '100%'),
    gridTemplateColumns: ({ gameLoaded }: any) =>
      gameLoaded ? 'repeat(2, min-content)' : 'repeat(2, 50%)',
    gridTemplateRows: '1fr 80px 60px',
    display: 'grid',
    gridRowGap: '1rem',
    overflow: 'hidden',
    background: `url(${bookBackground}) center center no-repeat`,
    backgroundSize: '100% 100%',
    backgroundColor: 'white',
    border: `1rem solid ${theme.palette.info.light}`,
    borderTop: 0,
    borderBottom: 0,
    boxShadow: theme.shadows[3],
    '@media (orientation: portrait)': {
      gridTemplateColumns: 'repeat(2, minmax(40vw, min-content))',
      height: '65vw',
    },
  },
  onePageBehavior: {
    background: 'white',
    '& > *:first-child': {
      gridColumn: '1 / 3',
    },
  },
  pageDescription: {
    padding: `0 ${theme.typography.pxToRem(24)}`,
    fontFamily: FONT_GSS_FUTURA_SEMI_BOLD,
    fontSize: theme.typography.pxToRem(28),
    lineHeight: theme.typography.pxToRem(48),
    minHeight: theme.typography.pxToRem(80),
  },
  pageNumber: {
    fontFamily: FONT_GSS_FUTURA_LIGHT,
    padding: `0 ${theme.typography.pxToRem(24)}`,
    lineHeight: theme.typography.pxToRem(48),
    fontSize: '1rem',
  },
}));

export default FindTheObjectActivity;
